import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Edit, FileText, Phone } from "lucide-react";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked } from "@/lib/roles";
import {
  CONTRACT_DOCS_BUCKET,
  useContract, useContractAssets, useContractRenewalLogs, useContractWorkOrders, useLogRenewal,
} from "@/features/contracts/api";
import { ContractStatusBadge, ContractTypeBadge, DaysToExpiry, SlaBadge, computeSla } from "@/features/contracts/badges";
import { EscalationSchedule } from "@/features/contracts/EscalationSchedule";

import { RENEWAL_DECISIONS, type RenewalDecision } from "@/features/contracts/types";
import { WoStatusBadge, SeverityBadge } from "@/features/work-orders/badges";
import { formatDate, fmtDateTime } from "@/features/assets/format";

const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);
const UNSCOPED_ROLES = new Set(["super_admin", "management", "finance", "quality"]);
const INVOICE_MANAGER_ROLES = new Set(["super_admin", "finance", "purchase_stores"]);



export const Route = createFileRoute("/_authenticated/contracts/$contractId/")({
  component: ContractDetailPage,
});

function ContractDetailPage() {
  const { contractId } = Route.useParams();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const { data: contract, isLoading } = useContract(contractId);
  const { data: assets = [] } = useContractAssets(contractId);
  const { data: wos = [] } = useContractWorkOrders(contractId);
  const { data: logs = [] } = useContractRenewalLogs(contractId);

  const roles = me?.roles ?? [];
  const canEdit = roles.some((r) => EDITOR_ROLES.has(r));
  const canManageInvoiceStatus = roles.some((r) => INVOICE_MANAGER_ROLES.has(r));
  const isUnscoped = roles.some((r) => UNSCOPED_ROLES.has(r));
  const locked = isBranchLocked(roles);
  const effectiveBranchId = locked ? (me?.profile?.branch_id ?? null) : (currentBranchId ?? null);

  if (isLoading) return <div className="py-20 text-center text-muted-foreground">Loading contract…</div>;
  if (!contract) return (
    <Card className="mx-auto max-w-lg"><CardContent className="space-y-2 p-6 text-center">
      <h2 className="text-lg font-semibold">You do not have access to this contract.</h2>
      <Button onClick={() => navigate({ to: "/contracts" })}>Back to Contracts</Button>
    </CardContent></Card>
  );

  // Branch-access guard. Super Admin / Management / Finance / Quality are unscoped.
  // Everyone else must match the effective branch via contract.branch_id OR via at least
  // one linked covered asset's branch_id.
  const matchesBranch =
    isUnscoped ||
    !effectiveBranchId ||
    contract.branch_id === effectiveBranchId ||
    assets.some((a) => a.asset?.branch_id === effectiveBranchId);

  if (!matchesBranch) {
    return (
      <Card className="mx-auto max-w-lg"><CardContent className="space-y-2 p-6 text-center">
        <h2 className="text-lg font-semibold">You do not have access to this contract.</h2>
        <Button onClick={() => navigate({ to: "/contracts" })}>Back to Contracts</Button>
      </CardContent></Card>
    );
  }


  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/contracts" })}>
        <ArrowLeft className="mr-1 h-4 w-4" />Back to Contracts
      </Button>

      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div className="space-y-1">
            <Badge variant="outline" className="font-mono">{contract.contract_no}</Badge>
            <h1 className="text-2xl font-semibold tracking-tight">{contract.vendor?.name ?? "—"}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <ContractTypeBadge value={contract.type} />
              <ContractStatusBadge value={contract.status} />
              <DaysToExpiry endDate={contract.end_date} />
              <span className="text-sm text-muted-foreground">{formatDate(contract.start_date)} → {formatDate(contract.end_date)}</span>
            </div>
          </div>
          {canEdit && (
            <Button variant="outline" onClick={() => navigate({ to: "/contracts/$contractId/edit", params: { contractId } })}>
              <Edit className="mr-1 h-4 w-4" />Edit
            </Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
          <TabsTrigger value="assets">Covered Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="pm">PM Visits</TabsTrigger>
          <TabsTrigger value="wo">Work Orders & SLA</TabsTrigger>
          <TabsTrigger value="renewal">Renewal Log ({logs.length})</TabsTrigger>
          <TabsTrigger value="docs">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <InfoCard title="Commercial">
              <Row k="Contract Value" v={contract.contract_value ? `₹${new Intl.NumberFormat("en-IN").format(contract.contract_value)}` : "—"} />
              <Row k="GST Rate" v={contract.gst_rate ? `${contract.gst_rate}%` : "—"} />
              <Row k="GST Amount" v={contract.gst_amount ? `₹${new Intl.NumberFormat("en-IN").format(contract.gst_amount)}` : "—"} />
              <Row k="Payment Terms" v={contract.payment_terms ?? "—"} />
            </InfoCard>
            <InfoCard title="SLA & Scope">
              <Row k="Response SLA" v={contract.response_sla_hours ? `${contract.response_sla_hours}h` : "—"} />
              <Row k="Resolution SLA" v={contract.resolution_sla_hours ? `${contract.resolution_sla_hours}h` : "—"} />
              <Row k="Uptime Guarantee" v={contract.uptime_guarantee_pct ? `${contract.uptime_guarantee_pct}%` : "—"} />
              <Row k="Spares Included" v={contract.spares_included ? "Yes" : "No"} />
              <Row k="PM Visits" v={`${contract.pm_visits_done} / ${contract.pm_visits_included ?? "—"}`} />
              <Row k="Coverage" v={contract.coverage_scope ?? "—"} />
              <Row k="Penalty" v={contract.penalty_clause ?? "—"} />
            </InfoCard>
            <InfoCard title="Vendor Contacts">
              <Row k="L1" v={contract.l1_name ? <span>{contract.l1_name}{contract.l1_phone && <a className="ml-2 inline-flex items-center text-primary hover:underline" href={`tel:${contract.l1_phone}`}><Phone className="mr-1 h-3 w-3" />{contract.l1_phone}</a>}</span> : "—"} />
              <Row k="L2" v={contract.l2_name ? <span>{contract.l2_name}{contract.l2_phone && <a className="ml-2 inline-flex items-center text-primary hover:underline" href={`tel:${contract.l2_phone}`}><Phone className="mr-1 h-3 w-3" />{contract.l2_phone}</a>}</span> : "—"} />
            </InfoCard>
            <InfoCard title="Renewal">
              <Row k="Decision" v={contract.renewal_decision ?? "—"} />
              <Row k="Notes" v={contract.renewal_notes ?? "—"} />
            </InfoCard>
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <EscalationSchedule
            contractId={contractId}
            escalationType={contract.escalation_type}
            canEdit={canEdit || canManageInvoiceStatus}
            canManageInvoiceStatus={canManageInvoiceStatus}
          />
        </TabsContent>

        <TabsContent value="assets">
          {(() => {
            const disposedCount = assets.filter((a) => a.asset?.status === "Disposed" || a.asset?.status === "Condemned").length;
            const activeCount = assets.length - disposedCount;
            return (
              <Card><CardContent className="p-0">
                <div className="flex items-center justify-between border-b px-4 py-2 text-xs text-muted-foreground">
                  <span>Active: <strong className="text-foreground">{activeCount}</strong> · Disposed: <strong className="text-foreground">{disposedCount}</strong></span>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Asset Code</TableHead><TableHead>Name</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {assets.length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No assets linked to this contract.</TableCell></TableRow>}
                    {assets.map((a) => {
                      const isDisposed = a.asset?.status === "Disposed";
                      return (
                        <TableRow key={a.id} className={isDisposed ? "opacity-70" : undefined}>
                          <TableCell className="font-mono text-xs">{a.asset?.asset_code ?? "—"}</TableCell>
                          <TableCell>
                            {a.asset?.name ?? "—"}
                            {isDisposed && <Badge variant="outline" className="ml-2">Disposed</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            {a.asset && (
                              <Link to="/assets/$assetId" params={{ assetId: a.asset.id }} className="text-primary text-xs hover:underline">View asset →</Link>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent></Card>
            );
          })()}
        </TabsContent>

        <TabsContent value="pm">
          <Card><CardContent className="space-y-2 p-4 text-sm">
            <div className="flex items-center justify-between">
              <span>PM Visits Completed</span>
              <Badge variant="outline">{contract.pm_visits_done} / {contract.pm_visits_included ?? "—"}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              PM visits are tracked against the work orders generated for assets covered by this contract.
              See <strong>Work Orders & SLA</strong> tab and the Preventive Maintenance module.
            </p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="wo">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>WO</TableHead><TableHead>Asset</TableHead><TableHead>Type</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead>
                <TableHead>Reported</TableHead><TableHead>Response</TableHead><TableHead>Resolution</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {wos.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">No work orders billed to this contract yet.</TableCell></TableRow>}
                {(wos as unknown as Array<{ id: string; wo_number: string; status: import("@/features/work-orders/types").WoStatus; severity: import("@/features/work-orders/types").WoSeverity; type: string; reported_at: string; acknowledged_at: string | null; resolved_at: string | null; asset: { id: string; asset_code: string; name: string } | null }>).map((w) => {
                  const resp = computeSla(w.reported_at, w.acknowledged_at, contract.response_sla_hours);
                  const res = computeSla(w.reported_at, w.resolved_at, contract.resolution_sla_hours);
                  return (
                    <TableRow key={w.id} className="cursor-pointer" onClick={() => navigate({ to: "/work-orders/$woId", params: { woId: w.id } })}>
                      <TableCell className="font-mono text-xs">{w.wo_number}</TableCell>
                      <TableCell>{w.asset?.name ?? "—"}</TableCell>
                      <TableCell>{w.type}</TableCell>
                      <TableCell><SeverityBadge value={w.severity} /></TableCell>
                      <TableCell><WoStatusBadge value={w.status} /></TableCell>
                      <TableCell className="text-xs">{formatDate(w.reported_at)}</TableCell>
                      <TableCell><SlaBadge state={resp.state} label="Resp" elapsedMinutes={resp.elapsedMinutes} /></TableCell>
                      <TableCell><SlaBadge state={res.state} label="Res" elapsedMinutes={res.elapsedMinutes} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="renewal" className="space-y-3">
          {canEdit && <LogRenewalDialog contractId={contractId} />}
          <Card><CardContent className="space-y-3 p-4">
            {logs.length === 0 && <p className="text-sm text-muted-foreground">No renewal decisions logged yet.</p>}
            {logs.map((l) => (
              <div key={l.id} className="rounded-md border bg-card p-3 text-sm">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{l.decision}</Badge>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(l.created_at)} · {l.actor?.full_name || l.actor?.email || "system"}</span>
                </div>
                {l.target_renewal_date && <div className="mt-1 text-xs">Target renewal: {formatDate(l.target_renewal_date)}</div>}
                {l.notes && <p className="mt-1 text-sm">{l.notes}</p>}
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="docs">
          <Card><CardContent className="space-y-3 p-4 text-sm">
            <ContractDocumentView documentUrl={contract.document_url} bucket={CONTRACT_DOCS_BUCKET} />
            {contract.notes && <div><div className="text-xs uppercase text-muted-foreground">Notes</div><p>{contract.notes}</p></div>}
          </CardContent></Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1 text-sm">{children}</CardContent>
    </Card>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs uppercase text-muted-foreground">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

function LogRenewalDialog({ contractId }: { contractId: string }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState<RenewalDecision>("Renew");
  const [notes, setNotes] = useState("");
  const [target, setTarget] = useState("");
  const log = useLogRenewal();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm">Log Renewal Decision</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Log renewal decision</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Decision</Label>
            <Select value={decision} onValueChange={(v) => setDecision(v as RenewalDecision)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RENEWAL_DECISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Target renewal date</Label>
            <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={log.isPending} onClick={async () => {
            try {
              await log.mutateAsync({ contract_id: contractId, decision, notes: notes || null, target_date: target || null });
              toast.success("Decision logged");
              setOpen(false); setNotes(""); setTarget("");
            } catch (e) { toast.error((e as Error).message); }
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractDocumentView({ documentUrl, bucket }: { documentUrl: string | null; bucket: string }) {
  const [viewerOpen, setViewerOpen] = useState(false);

  if (!documentUrl) {
    return <p className="text-muted-foreground">No contract document uploaded.</p>;
  }

  const fileName = documentUrl.split("/").pop() ?? "contract-document";

  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" onClick={() => setViewerOpen(true)}>
        <FileText className="mr-1 h-4 w-4" />
        View Document
      </Button>
      <p className="break-all text-xs text-muted-foreground">{documentUrl}</p>
      <DocumentViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        bucket={bucket}
        path={documentUrl}
        fileName={fileName}
      />
    </div>
  );
}

